import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  AbsintheSocketManager,
  type SocketFactory,
  type SocketStatus,
} from './socket';

// React binding for the Absinthe-over-Phoenix subscription client (socket.ts).
//
// `useSubscription` opens (or reuses) the shared, ref-counted socket, starts a GraphQL
// subscription, and calls `onData` for every push. It reads the operator JWT from useAuth().token
// (the same token the GraphQL fetch layer uses) — the comment in AuthContext confirms C29 reuses
// it here. WITHOUT a token we do NOT connect (the backend would reject it anyway) and the hook
// yields no data instead of crashing.
//
// The shared manager is keyed by token at module scope so that multiple mounted subscriptions
// (e.g. the usage feed + the provider-health board) share ONE socket. The hook ref-counts via the
// manager: when the last subscriber across the app unmounts, the manager disconnects the socket.

/** Module-scoped manager registry, keyed by token, so all hooks share one socket per token. */
let sharedManager: AbsintheSocketManager | null = null;
let sharedManagerToken: string | null = null;
/** Count of live hook subscribers on the current shared manager (for recreate-on-empty). */
let sharedSubscriberCount = 0;

/** Injectable factory for tests (so a fake Socket can be supplied without a real WebSocket). */
let socketFactoryOverride: SocketFactory | undefined;

/**
 * Override the Phoenix Socket factory used by the shared manager (test seam). Pass `undefined`
 * to restore the default. Resets the shared manager so the next subscribe picks up the override.
 */
export function __setSocketFactoryForTests(factory: SocketFactory | undefined): void {
  socketFactoryOverride = factory;
  sharedManager = null;
  sharedManagerToken = null;
  sharedSubscriberCount = 0;
}

function getManager(token: string): AbsintheSocketManager {
  if (sharedManager && sharedManagerToken === token) return sharedManager;
  sharedManager = new AbsintheSocketManager(token, socketFactoryOverride);
  sharedManagerToken = token;
  return sharedManager;
}

export interface UseSubscriptionOptions<TData> {
  /** Called for each subscription push, with the typed payload. */
  onData: (data: TData) => void;
  /** Subscription variables (e.g. a UsageFilter). Serialized to re-subscribe on change. */
  variables?: Record<string, unknown> | undefined;
  /** When false, the subscription is not started (e.g. while live updates are paused). */
  enabled?: boolean | undefined;
}

export interface UseSubscriptionState {
  /** Current socket status, for the live/degraded/reconnect affordance. */
  status: SocketStatus;
  /** True while the socket is open and this subscription is active. */
  connected: boolean;
  /**
   * Forcibly tear down + reopen the shared WebSocket and re-subscribe every active
   * subscription. Backs the live indicator's click affordance — when an operator
   * suspects the feed has silently stalled, clicking the indicator calls this to
   * cycle the connection. A no-op when no token is set.
   */
  reconnect: () => void;
}

/**
 * Subscribe to a GraphQL subscription document. Starts on mount (when enabled + authenticated) and
 * re-subscribes when the document or variables change. Cleans up on unmount: the subscription is
 * removed and — when it is the last one — the shared socket disconnects.
 */
export function useSubscription<TData>(
  query: string,
  { onData, variables, enabled = true }: UseSubscriptionOptions<TData>,
): UseSubscriptionState {
  const { token } = useAuth();
  const [status, setStatus] = useState<SocketStatus>('closed');

  // Keep the latest onData without forcing a re-subscribe on every render.
  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  const variablesKey = JSON.stringify(variables ?? {});

  useEffect(() => {
    // No token → do not connect (backend rejects unauthenticated sockets). Yield no data.
    if (!token || !enabled) {
      setStatus('closed');
      return;
    }

    const manager = getManager(token);
    sharedSubscriberCount += 1;

    const offStatus = manager.onStatusChange(setStatus);
    setStatus(manager.getStatus());

    const handle = manager.subscribe<TData>(query, variables ?? {}, {
      onData: (data) => onDataRef.current(data),
    });

    return () => {
      offStatus();
      handle.unsubscribe();
      sharedSubscriberCount -= 1;
      // When the app-wide last subscriber leaves, drop the shared manager so a later mount makes a
      // fresh socket (and any token change is picked up).
      if (sharedSubscriberCount <= 0) {
        sharedSubscriberCount = 0;
        sharedManager = null;
        sharedManagerToken = null;
      }
    };
    // `variablesKey` captures variables by value; query/token/enabled are primitives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, variablesKey, token, enabled]);

  function reconnect(): void {
    if (!token) return;
    const manager = getManager(token);
    manager.reconnect();
  }

  return { status, connected: status === 'open', reconnect };
}
